import { Link } from 'react-router-dom';
import { IconBook, IconTrophy, IconLeaf, IconBuilding, IconCheck, IconArrowRight } from '../components/Icons';
import { 
  LogoEU, LogoDoS, LogoUNDP, LogoASEAN, 
  LogoFord, LogoLuce, LogoCarnegie, LogoGreenpeace, 
  LogoTNC, LogoGlobethics, LogoUGM, LogoWCC 
} from '../components/PartnerLogos';
import { 
  BrandInstagram, BrandFacebook, BrandLinkedIn, 
  BrandScopus, BrandORCID, BrandWebOfScience,
  BrandResearchGate, BrandAcademia, BrandSINTA, BrandGoogleScholar 
} from '../components/BrandIcons';
import useScrollReveal from '../hooks/useScrollReveal';
import './Home.css';

const socialLinks = [
  { label: 'Instagram',   href: 'https://www.instagram.com/sofjandicky/',           icon: BrandInstagram },
  { label: 'Facebook',    href: 'https://web.facebook.com/dicky.sofjan/',            icon: BrandFacebook },
  { label: 'LinkedIn',    href: 'https://www.linkedin.com/in/dicky-sofjan-b11789118/',           icon: BrandLinkedIn },
];

const academicLinks = [
  { label: 'Scopus ID',      href: 'https://www.scopus.com/authid/detail.uri?authorId=6503908870',                           icon: BrandScopus },
  { label: 'ORCID',          href: 'https://orcid.org/0000-0002-3162-206X',                                                    icon: BrandORCID },
  { label: 'WOS ID',         href: 'https://www.webofscience.com/wos/author/record/GPP-4658-2022',                             icon: BrandWebOfScience },
  { label: 'ResearchGate',   href: 'https://www.researchgate.net/profile/Dicky-Sofjan',                                        icon: BrandResearchGate },
  { label: 'Academia.edu',   href: 'https://ugm.academia.edu/DSofjan',                                                         icon: BrandAcademia },
  { label: 'Google Scholar', href: 'https://scholar.google.com/citations?user=HpxRHN0AAAAJ&hl=en',                                 icon: BrandGoogleScholar },
  { label: 'SINTA',          href: 'https://sinta.kemdiktisaintek.go.id/authors/profile/6007235/?view=rama',                   icon: BrandSINTA },
];

const stats = [
  { value: '20+',  label: 'Years International Experience' },
  { value: '100+', label: 'International Conferences' },
  { value: '40+',  label: 'Countries Visited' },
  { value: '40+',  label: 'Major Publications' },
];

const highlights = [
  { Icon: IconBook,        title: 'Research & Publications',   desc: 'Explore a decades-spanning portfolio of scholarly works, from interreligious studies to global ethics, published by prestigious international academic houses.', to: '/research',      cta: 'Browse Publications' },
  { Icon: IconTrophy,      title: 'Awards & Grants',           desc: 'Recognizing excellence through prestigious international research grants and transformative partnerships with major global foundations.', to: '/awards',      cta: 'View Awards' },
  { Icon: IconLeaf,        title: 'Nglanggeran Eco-Village',   desc: 'A peaceful retreat on the edge of the Pentung River, offering a thoughtful escape into nature and authentic Javanese village life.', to: '/eco-village',  cta: 'Explore the Village' },
  { Icon: IconBuilding,    title: 'Organizations & Vice Presidency', desc: 'Associate Professor at UGM, Founder/President of two development foundations, and Vice President of Geneva-based Globethics, Switzerland.', to: '/organizations', cta: 'View Institutional Roles' },
];

export default function Home() {
  useScrollReveal();

  return (
    <main>
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="home-hero" aria-labelledby="home-hero-heading">
        <div className="home-hero__bg" />
        <div className="container home-hero__content animate-fade-up">
          <div className="home-hero__text">
            <h1 id="home-hero-heading" style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', lineHeight: 1.05 }}>
              <span className="home-hero__name">Dicky Sofjan</span>
              <span className="home-hero__title">MPP, M.A., PH.D.</span>
            </h1>
            <p className="home-hero__sub">
              Associate Professor, Scholar, Ethicist,<br />
              Intellectual-Activist and Social Entrepreneur
            </p>
            <div className="home-hero__cta">
              <Link to="/about"   className="btn btn-gold">Explore My Work</Link>
              <Link to="/contact" className="btn btn-white" style={{ background: 'transparent', border: '2px solid rgba(255,255,255,0.4)', color: '#fff' }}>Get in Touch</Link>
            </div>
          </div>
          
          <div className="home-about__img-wrap" style={{ display: 'none' }}>
             {/* Mobile hides this image, displayed on desktop via CSS */}
          </div>
          <div className="home-hero__img-container reveal reveal-photo delay-2">
            <div className="home-about__img-wrap">
              <img
                src="/1.jpeg"
                alt="Dr. Dicky Sofjan speaking at Global Forum on Depolarization"
                className="home-about__img"
                style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.5)', aspectRatio: '8/9' }}
                loading="eager"
              />

            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────── */}
      <div className="home-stats">
        <div className="container">
          <div className="home-stats__grid">
            {stats.map((s, i) => (
              <div key={s.label} className={`home-stats__item reveal reveal-up delay-${i + 1}`}>
                <span className="home-stats__value">{s.value}</span>
                <span className="home-stats__label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── About preview ────────────────────────────────── */}
      <section className="section" aria-labelledby="home-about-heading">
        <div className="container">
          <div className="home-about__grid home-about__grid--with-img">
            <div className="home-about__img-col reveal reveal-photo">
              <div className="home-about__img-wrap">
                <img
                  src="/dr sofjan/4.jpeg"
                  alt="Dr. Dicky Sofjan reading"
                  className="home-about__img"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="home-about__text reveal reveal-up">
              <span className="section-label">About Dr. Sofjan</span>
              <h2 id="home-about-heading" className="section-title">
                Global Thought Leader & Community Innovator
              </h2>
              <p>
                Dr. Sofjan’s work sits at the intersection of academic research and social advocacy. His scholarship explores the complex ties between religion, politics, and sustainability—with a particular focus on Islamic ethics. This expertise has made him a trusted advisor to government bodies both in Indonesia and abroad. As a founder driven by ethical practice, he bridges the gap between theory and action, leveraging a global network of development agencies and philanthropic organizations to drive meaningful change.
              </p>
              <div className="home-about__tags">
                {['UGM Graduate School', 'Globethics, Geneva', 'Depolarization Practitioner', 'Yadema Foundation', 'YKPAI Foundation'].map(t => (
                  <span key={t} className="badge badge-navy">{t}</span>
                ))}
              </div>
              <Link to="/about" className="btn btn-primary">Read Full Biography</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Highlights ───────────────────────────────────── */}
      <section className="section section-alt" aria-labelledby="home-highlights-heading">
        <div className="container">
          <div className="text-center reveal reveal-up" style={{ marginBottom: '3rem' }}>
            <span className="section-label">Areas of Work</span>
            <h2 id="home-highlights-heading" className="section-title">Key Areas of Impact</h2>
          </div>
          <div className="grid-4">
            {highlights.map((h, i) => (
              <Link key={h.title} to={h.to} className={`home-highlight-card reveal reveal-scale delay-${i + 1}`}>
                <div className="home-highlight-card__icon"><h.Icon size={32} color="currentColor" /></div>
                <h3 className="home-highlight-card__title">{h.title}</h3>
                <p className="home-highlight-card__desc">{h.desc}</p>
                <span className="home-highlight-card__cta">{h.cta} <IconArrowRight size={14} color="currentColor" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Eco-Village teaser ───────────────────────────── */}
      <section className="home-eco" aria-labelledby="home-eco-heading">
        <div className="container">
          <div className="home-eco__grid">
            <div className="reveal reveal-up">
              <span className="section-label" style={{ color: '#86efac' }}>Latest Initiative</span>
              <h2 id="home-eco-heading" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.8rem,3.5vw,2.6rem)', color: '#fff', marginBottom: '1rem' }}>
                Nglanggeran<br />Eco-Village
              </h2>

              <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '2rem', maxWidth: 460, lineHeight: 1.8 }}>
                Nglanggeran Eco-Village sits on the edge of Pentung River in Pathuk, Gunung Kidul, which is only 45 minutes away from the City of Yogyakarta. We offer 10 rustic designed, wooden villas that would surely inspire your creativity and bring you to your wildest imagination about beauty.
              </p>
              <Link to="/eco-village" className="btn btn-gold">Discover the Eco-Village</Link>
            </div>
            <div className="home-eco__img-col reveal reveal-photo">
              <img
                src="/ecovillage/1.jpeg"
                alt="Nglanggeran Eco-Village"
                className="home-eco__img"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Social Media & Profiles ───────────────────────── */}
      <section className="section" aria-labelledby="home-connect-heading">
        <div className="container text-center">
          <span className="section-label">Connect</span>
          <h2 id="home-connect-heading" className="section-title" style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
            Find Dr. Sofjan Online
          </h2>
          <p style={{ color: 'var(--text-light)', marginBottom: '2.5rem', fontSize: '0.9rem' }}>Follow his work across social media and academic platforms</p>

          <div className="profiles-group">
            <h3 className="profiles-group__title">Social Media</h3>
            <div className="profiles-row">
              {socialLinks.map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="profile-chip profile-chip--social">
                  <span className="profile-chip__abbr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <s.icon size={20} />
                  </span>
                  <span>{s.label}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="profiles-group" style={{ marginTop: '2rem' }}>
            <h3 className="profiles-group__title">Academic Profiles</h3>
            <div className="profiles-row">
              {academicLinks.map(a => (
                <a key={a.label} href={a.href} target="_blank" rel="noopener noreferrer" className="profile-chip profile-chip--academic">
                  <span className="profile-chip__abbr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <a.icon size={20} />
                  </span>
                  <span>{a.label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Supporters ───────────────────────────────────── */}
      <section className="section section-alt" aria-labelledby="home-supporters-heading">
        <div className="container">
          <div className="text-center reveal reveal-up" style={{ marginBottom: '3rem' }}>
            <span className="section-label">Institutional Support</span>
            <h2 id="home-supporters-heading" className="section-title">Supported by Leading Global Institutions</h2>
            <p style={{ color: 'var(--text-light)', maxWidth: '700px', margin: '0 auto', fontSize: '0.9rem' }}>
              Dr. Sofjan's work has been supported by a global network of bilateral agencies, foundations, and academic bodies.
            </p>
          </div>
          
          <div className="home-supporters">
            <div className="home-supporters__grid">
              <div className="home-supporter-box reveal reveal-scale delay-1"><LogoEU size={70} /></div>
              <div className="home-supporter-box reveal reveal-scale delay-2"><LogoDoS size={70} /></div>
              <div className="home-supporter-box reveal reveal-scale delay-3"><LogoUNDP size={70} /></div>
              <div className="home-supporter-box reveal reveal-scale delay-4"><LogoASEAN size={70} /></div>
              <div className="home-supporter-box reveal reveal-scale delay-1"><LogoFord size={70} /></div>
              <div className="home-supporter-box reveal reveal-scale delay-2"><LogoLuce size={70} /></div>
              <div className="home-supporter-box reveal reveal-scale delay-3"><LogoCarnegie size={70} /></div>
              <div className="home-supporter-box reveal reveal-scale delay-4"><LogoGreenpeace size={70} /></div>
              <div className="home-supporter-box reveal reveal-scale delay-1"><LogoTNC size={70} /></div>
              <div className="home-supporter-box reveal reveal-scale delay-2"><LogoGlobethics size={70} /></div>
              <div className="home-supporter-box reveal reveal-scale delay-3"><LogoUGM size={70} /></div>
              <div className="home-supporter-box reveal reveal-scale delay-4"><LogoWCC size={70} /></div>
            </div>
          </div>
          
          <div className="text-center" style={{ marginTop: '3rem' }}>
            <Link to="/partnerships" className="btn btn-outline btn-sm">View All Partnerships →</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
